import { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import '../utils/pdfWorkerConfig'; // Initialize PDF.js worker configuration

function PDFViewer({ document, hideMetadata = false }) {
  console.log('[PDFViewer] render', {
    docId: document?.id,
  });

  const canvasRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.5);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMetadata, setShowMetadata] = useState(!hideMetadata);

  // Load PDF document
  useEffect(() => {
    if (!document) {
      setIsLoading(false);
      return;
    }

    let localPdfDoc = null;

    const loadPDF = async () => {
      try {
        setIsLoading(true);
        setError(null);

        let pdfUrl;
        if (document.downloadUrl) {
          // Document from backend - use signed URL
          pdfUrl = document.downloadUrl;
          console.log('Using signed URL:', pdfUrl);
        } else if (document.file) {
          // Newly uploaded file
          pdfUrl = URL.createObjectURL(document.file);
          console.log('Using blob URL for new file:', pdfUrl);
        } else {
          console.error('Document object:', document);
          throw new Error('No file available to load - missing downloadUrl');
        }

        console.log('Loading PDF from:', pdfUrl);

        // Configure loading task with explicit worker enabled
        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          disableWorker: false,
          httpHeaders: {},
          withCredentials: false,
          // CVE-2024-4367: see utils/pdfSafety.js.
          isEvalSupported: false,
        });

        // Handle worker setup errors
        loadingTask.onPassword = (callback, reason) => {
          console.log('PDF requires password:', reason);
          setError('This PDF is password protected');
        };

        const pdf = await loadingTask.promise;
        localPdfDoc = pdf;

        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        setCurrentPage(1);
        setIsLoading(false);

        console.log('PDF loaded successfully:', pdf.numPages, 'pages');
      } catch (err) {
        console.error('[PDF Viewer] Error loading document:', {
          document,
          error: err
        });
        
        // Check if it's a worker error and provide helpful message
        if (err.message.includes('worker') || err.message.includes('Worker')) {
          setError('PDF worker failed to load. Please refresh the page and try again.');
        } else {
          setError(err.message);
        }
        setIsLoading(false);
      }
    };

    loadPDF();

    return () => {
      if (localPdfDoc) localPdfDoc.destroy();
    };
  }, [document]);

  // Render current page
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    let renderTask = null;
    let isCancelled = false;

    const renderPage = async () => {
      try {
        const page = await pdfDoc.getPage(currentPage);
        
        if (isCancelled) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d');
        const viewport = page.getViewport({ scale });

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        // Cancel any existing render task
        if (renderTask) {
          renderTask.cancel();
        }

        renderTask = page.render(renderContext);
        
        await renderTask.promise;
        
        if (!isCancelled) {
          console.log('Page rendered:', currentPage);
        }
      } catch (err) {
        if (err.name === 'RenderingCancelledException') {
          console.log('Rendering cancelled');
        } else {
          console.error('Error rendering page:', err);
          if (!isCancelled) {
            setError(err.message);
          }
        }
      }
    };

    renderPage();

    // Cleanup function
    return () => {
      isCancelled = true;
      if (renderTask) {
        renderTask.cancel();
      }
    };
  }, [pdfDoc, currentPage, scale]);

  const goToPage = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
    }
  };

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.25, 3));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.25, 0.5));
  const resetZoom = () => setScale(1.5);

  const downloadPDF = () => {
    if (document.downloadUrl) {
      const link = window.document.createElement('a');
      link.href = document.downloadUrl;
      link.download = document.filename;
      link.target = '_blank'; // Open in new tab for signed URLs
      link.click();
    } else {
      alert('Download URL not available');
    }
  };

  if (!document) {
    return (
      <div className="pdf-viewer-empty">
        <p>No document selected</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="pdf-viewer-loading">
        <div className="spinner"></div>
        <p>Loading PDF...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdf-viewer-error">
        <p>Error loading PDF: {error}</p>
      </div>
    );
  }

  return (
    <div className="pdf-viewer">
      {/* Toolbar */}
      <div className="pdf-toolbar">
        <div className="pdf-toolbar-section">
          <button
            className="pdf-btn"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
            title="Previous Page"
          >
            ◀
          </button>
          <span className="pdf-page-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="pdf-btn"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= totalPages}
            title="Next Page"
          >
            ▶
          </button>
        </div>

        <div className="pdf-toolbar-section">
          <button className="pdf-btn" onClick={zoomOut} title="Zoom Out">
            −
          </button>
          <span className="pdf-zoom-info">{Math.round(scale * 100)}%</span>
          <button className="pdf-btn" onClick={zoomIn} title="Zoom In">
            +
          </button>
          <button className="pdf-btn" onClick={resetZoom} title="Reset Zoom">
            Reset
          </button>
        </div>

        <div className="pdf-toolbar-section">
          <button className="pdf-btn" onClick={downloadPDF} title="Download">
            ⬇ Download
          </button>
          {!hideMetadata && (
            <button 
              className={`pdf-btn ${showMetadata ? 'active' : ''}`}
              onClick={() => setShowMetadata(!showMetadata)} 
              title="Toggle Metadata Panel"
            >
              {showMetadata ? '📋 Hide Info' : '📋 Show Info'}
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="pdf-content">
        {/* Canvas Container */}
        <div className="pdf-canvas-container">
          <canvas ref={canvasRef} className="pdf-canvas" />
        </div>

        {/* Metadata Panel */}
        {showMetadata && (
          <div className="metadata-panel">
            <div className="metadata-panel-header">
              <h3 style={{ margin: 0 }}>Document Information</h3>
            </div>

            <div className="metadata-panel-body">
              <div className="metadata-panel-content">
                {/* Document Info */}
                <div className="doc-info-section">
                  <div className="doc-info-row">
                    <span className="doc-info-label">Filename:</span>
                    <span className="doc-info-value">{document.filename}</span>
                  </div>
                  <div className="doc-info-row">
                    <span className="doc-info-label">Uploaded:</span>
                    <span className="doc-info-value">
                      {document.uploadDate ? new Date(document.uploadDate).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div className="doc-info-row">
                    <span className="doc-info-label">Size:</span>
                    <span className="doc-info-value">{document.size ? `${Math.round(document.size / 1024)} KB` : 'N/A'}</span>
                  </div>
                  <div className="doc-info-row">
                    <span className="doc-info-label">Category:</span>
                    <span className="doc-info-value">{document.category || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PDFViewer;
